import {
  getEditorStateMapEntry,
  resolveStampedEditorStateMapValue,
} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {
  PlanningCommentableRichText,
  type PlanningCommentableRichTextProps,
} from 'components/DSYS/editor/dynamicNode/PlanningCommentableRichText';
import {
  buildStructuredCommentLexicalValue,
  buildStructuredCommentPlainText,
  extractStructuredCommentAnchorsFromLexicalJson,
  parseStructuredCommentState,
  serializeStructuredCommentState,
  type StructuredCommentLine,
} from 'components/DSYS/editor/dynamicNode/structuredCommentState';
import {STRUCTURED_COMMENT_MULTI_BLOCK_CLASS} from 'components/DSYS/editor/structuredCommentSelection';
import {Suspense} from 'react';

type PersistStructuredCommentKey = (
  key: string,
  serialized: string | undefined,
  sourceText: string | undefined
) => void;

type PlanningStructuredCommentFieldProps = Omit<
  PlanningCommentableRichTextProps,
  'value' | 'onChange'
> & {
  mapKey: string;
  mapJson: string | undefined;
  lines: StructuredCommentLine[];
  onPersistKey: PersistStructuredCommentKey;
};

export const PlanningStructuredCommentField = ({
  mapKey,
  mapJson,
  lines,
  onPersistKey,
  contentClassName,
  ...rest
}: PlanningStructuredCommentFieldProps) => {
  const fallbackText = buildStructuredCommentPlainText(lines);
  const fallbackValue = buildStructuredCommentLexicalValue(lines, []);
  const entry = getEditorStateMapEntry(mapJson, mapKey);
  const structuredState = parseStructuredCommentState(entry);
  const value = structuredState
    ? buildStructuredCommentLexicalValue(lines, structuredState.anchors)
    : resolveStampedEditorStateMapValue(mapJson, mapKey, fallbackValue);

  return (
    <Suspense fallback={fallbackText}>
      <PlanningCommentableRichText
        value={value}
        contentClassName={[STRUCTURED_COMMENT_MULTI_BLOCK_CLASS, contentClassName]
          .filter(Boolean)
          .join(' ')}
        onChange={(state) => {
          const anchors = extractStructuredCommentAnchorsFromLexicalJson(state.toString(), lines);
          onPersistKey(
            mapKey,
            anchors.length > 0 ? serializeStructuredCommentState(anchors) : undefined,
            fallbackText
          );
        }}
        {...rest}
      />
    </Suspense>
  );
};
